import { Checkbox, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { i18n } from 'i18n';
import { Trans } from '@lingui/react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import List from '@mui/material/List';
import TextField from '@mui/material/TextField';
import { StopeBackfillSummaryDto, StopeLocationDto } from 'providers/api';
import React from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as RWList, ListChildComponentProps } from 'react-window';
import StopeListItem, { StopeProfile } from './StopeListItem';
import SpoolDateFilterField from './SpoolDateFilterField';
import { Model } from '../../hooks/useModelState';
import PCStope from '../../MineModel/Stopes/PCStope';
import { PCNode } from '../../MineModel/Nodes';
import { levelAreaOptions, nodeMatchesLevelArea } from '../../MineModel/levelAreaFilter';
import { SceneProps } from '../../Scenes/scene-utils';

type RowData = StopeProfile[];

const MAX_NODE_OPTIONS = 50;

export interface StopeListFilters {
  filterStopeName: string;
}

interface StopeListProps {
  model: Model;
  activeNodeId: string | undefined;
  onNodePick: (nodeId?: string) => void;
  onSelect: (model: Model, node: PCNode, props: SceneProps, stopeId?: string) => void;
  onHover: (stopeLocation?: StopeLocationDto) => void;
  props: SceneProps;
}

const createNameFilter = (filterStopeName: string) => (stopes: Record<string, PCStope[]>) => {
  if (!filterStopeName) return stopes;
  return Object.entries(stopes).reduce((acc, [key, stopeList]) => {
    const filteredStopeList = stopeList.filter((stope) => stope.data.stopeName?.toLowerCase().includes(filterStopeName.toLowerCase()));
    return { ...acc, [key]: filteredStopeList };
  }, {} as Record<string, PCStope[]>);
};

const createCompletedFilter = (hideCompleted: boolean, mineOverview: StopeBackfillSummaryDto[]) => {
  const completedStopeIds = new Set(
    mineOverview
      .filter((stopeSummary) => stopeSummary.isComplete)
      .map((stopeSummary) => stopeSummary.stopeIdentifier.stopeId),
  );

  return (stopes: Record<string, PCStope[]>) => {
    if (!hideCompleted || completedStopeIds.size === 0) return stopes;
    return Object.entries(stopes).reduce((acc, [key, stopeList]) => {
      const filteredStopeList = stopeList.filter((stope) => !completedStopeIds.has(stope.data.stopeId));
      return { ...acc, [key]: filteredStopeList };
    }, {} as Record<string, PCStope[]>);
  };
};

// The empty option is "no value", matching stopes, nodes and spools without a tag.
const tagOptionLabel = (option: string) => option || i18n._('None');

const StopeList = ({
  model,
  activeNodeId,
  onNodePick,
  onSelect,
  onHover,
  props,
}: StopeListProps) => {
  const [checked, setChecked] = React.useState(false);
  const [stopeListFilters, setStopeListFilters] = React.useState<StopeListFilters>({ filterStopeName: '' });
  const { levelAreaFilter } = model;

  const pickedNode: PCNode | undefined = activeNodeId ? model.nodes?.[activeNodeId] : undefined;
  // A picked node narrows the list by itself; its name is in the search box but is not a stope name to match.
  const stopeNameQuery = pickedNode ? '' : stopeListFilters.filterStopeName;

  const allStopesArray: PCStope[] = React.useMemo(() => {
    if (!model.stopes || typeof model.stopes !== 'object') return [];
    return Object.values(model.stopes).flat();
  }, [model.stopes]);

  // package the info for each stope from three different DTOs into a more manageable single array
  const stopeLoc: PCStope[] = React.useMemo(() => (activeNodeId
    ? allStopesArray.filter((stope: PCStope) => stope.originNode?.id === activeNodeId)
    : allStopesArray
  ), [allStopesArray, activeNodeId]);

  const stopeProfiles: StopeProfile[] = React.useMemo(() => stopeLoc.map((pcStope: PCStope) => {
    const stopeLocation: StopeLocationDto = {
      stopeId: pcStope.data.stopeId,
      nodeId: pcStope.originNode?.id ?? '',
    };

    const stopeData = pcStope.data;
    const stopeSummary = model.mineOverview?.find((backfill) => backfill.stopeIdentifier.stopeId === stopeData.stopeId);

    return {
      nodeId: stopeLocation.nodeId,
      stopeId: stopeLocation.stopeId,
      stopeIdentifier: stopeSummary?.stopeIdentifier ?? null,
      totalCount: stopeSummary?.totalCount ?? null,
      plannedCount: stopeSummary?.plannedCount ?? null,
      completedCount: stopeSummary?.completedCount ?? null,
      plannedVolume: stopeSummary?.plannedVolume ?? null,
      completedVolume: stopeSummary?.completedVolume ?? null,
      nextPourId: stopeSummary?.nextPourId ?? null,
      nextPourDate: stopeSummary?.nextPourDate ?? null,
      isComplete: stopeSummary?.isComplete ?? null,
      stopeName: stopeData?.stopeName ?? null,
      references: stopeData?.references ?? null,
      pipeArrays: stopeData?.pipeArrays ?? null,
      volume: stopeData?.volume ?? null,
      physicalPropertiesUpdated: stopeData?.physicalPropertiesUpdated ?? null,
      routeUpdated: stopeData?.routeUpdated ?? null,
      level: stopeData?.level ?? null,
      area: stopeData?.area ?? null,
      model,
    };
  }), [stopeLoc, model.mineOverview]);

  const aggregatedLevelsAndAreas = React.useMemo(
    () => levelAreaOptions(stopeProfiles, Object.values(model.nodes || {}), Object.values(model.spools || {})),
    [stopeProfiles, model.nodes, model.spools],
  );

  const nodeOptions: PCNode[] = React.useMemo(() => {
    const query = stopeNameQuery.trim().toLowerCase();
    if (!query) return [];
    return Object.values(model.nodes || {})
      .filter((node) => node.nodeName.toLowerCase().includes(query) && nodeMatchesLevelArea(node, levelAreaFilter))
      .sort((a, b) => a.nodeName.localeCompare(b.nodeName, undefined, { numeric: true }))
      .slice(0, MAX_NODE_OPTIONS);
  }, [model.nodes, stopeNameQuery, levelAreaFilter]);

  const nameFilter = React.useMemo(() => createNameFilter(stopeNameQuery), [stopeNameQuery]);
  const completedFilter = React.useMemo(() => createCompletedFilter(checked, model.mineOverview), [checked, model.mineOverview]);

  const modelRef = React.useRef(model);

  const availableStopes = stopeProfiles
    .filter((stope) => Object.values(model.stopes || {}).some((stopeArray) => stopeArray.some((s) => s.data.stopeId === stope.stopeId)))
    .filter((stope) => !checked || !stope.isComplete);

  const handleStopeCheckbox = (isChecked: boolean) => {
    setChecked(isChecked);
  };

  const handleSearchInput = (value: string, reason: string) => {
    if (reason === 'reset') return;
    if (pickedNode) onNodePick(undefined);
    setStopeListFilters((existingFilters) => ({ ...existingFilters, filterStopeName: reason === 'clear' ? '' : value }));
  };

  const handleSearchPick = (value: PCNode | string | null) => {
    if (value && typeof value !== 'string') {
      setStopeListFilters((existingFilters) => ({ ...existingFilters, filterStopeName: '' }));
      onNodePick(value.id);
    } else if (!value) {
      onNodePick(undefined);
    }
  };

  const ListAny = RWList as unknown as React.ComponentType<any>;

  function Row({ index, style, data }: ListChildComponentProps<RowData>) {
    const stope = data[index];
    return (
      <div style={style}>
        <StopeListItem
          onSelect={onSelect}
          onHover={onHover}
          props={props}
          {...stope}
        />
      </div>
    );
  }

  React.useEffect(() => {
    modelRef.current = model;
  }, [model]);

  React.useEffect(() => {
    const currentModel = modelRef.current;
    currentModel.addFilter('stopes', 'filterStopeName', nameFilter);
    currentModel.addFilter('stopes', 'hideCompletedStopes', completedFilter);

    return () => {
      currentModel.removeFilter('stopes', 'filterStopeName');
      currentModel.removeFilter('stopes', 'hideCompletedStopes');
    };
  }, [nameFilter, completedFilter]);

  return (
    <Box
      sx={{
        flexGrow: 1,
        paddingBottom: 4,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Grid container spacing={1.5} sx={{ pt: 1 }}>
        <Grid size={6}>
          <Autocomplete
            id="level-filter-input"
            options={['', ...aggregatedLevelsAndAreas.levelOptions]}
            getOptionLabel={tagOptionLabel}
            value={levelAreaFilter.levels}
            autoHighlight
            multiple
            limitTags={2}
            renderInput={(params) => (
              <TextField
                {...params}
                label={i18n._('Level')}
                variant="outlined"
                size="small"
                color="secondary"
              />
            )}
            onChange={(event, newValue) => model.setLevelAreaFilter({ ...levelAreaFilter, levels: newValue })}
          />
        </Grid>
        <Grid size={6}>
          <Autocomplete
            id="area-filter-input"
            options={['', ...aggregatedLevelsAndAreas.areaOptions]}
            getOptionLabel={tagOptionLabel}
            value={levelAreaFilter.areas}
            autoHighlight
            multiple
            limitTags={2}
            renderInput={(params) => (
              <TextField
                {...params}
                label={i18n._('Area')}
                variant="outlined"
                size="small"
                color="secondary"
              />
            )}
            onChange={(event, newValue) => model.setLevelAreaFilter({ ...levelAreaFilter, areas: newValue })}
          />
        </Grid>
        <Grid size={12}>
          <SpoolDateFilterField filter={model.spoolDateFilter} onChange={model.setSpoolDateFilter} />
        </Grid>
        <Grid size={12} sx={{ mt: -1 }}>
          <Typography variant="caption" color="text.secondary">
            {i18n._('Hides stopes, nodes and spools outside these filters')}
          </Typography>
        </Grid>
        <Grid size={12}>
          <Autocomplete<PCNode, false, false, true>
            id="stope-node-search"
            freeSolo
            options={nodeOptions}
            groupBy={() => i18n._('Nodes')}
            filterOptions={(options) => options}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.nodeName)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={pickedNode ?? null}
            inputValue={pickedNode ? pickedNode.nodeName : stopeListFilters.filterStopeName}
            onInputChange={(event, value, reason) => handleSearchInput(value, reason)}
            onChange={(event, value) => handleSearchPick(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={i18n._('Search stopes or nodes')}
                variant="outlined"
                size="small"
                color="secondary"
              />
            )}
          />
        </Grid>
        <Grid size={12}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={checked}
              onChange={(event) => {
                handleStopeCheckbox(event.target.checked);
              }}
              color="secondary"
              size="small"
            />
            <Typography variant="subtitle1"><Trans id="Hide Completed Stopes">Hide Completed Stopes</Trans></Typography>
          </Box>
        </Grid>
      </Grid>
      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        {pickedNode
          ? i18n._('Stopes on {node} ({count})', { node: pickedNode.nodeName, count: availableStopes.length })
          : i18n._('Stopes ({count})', { count: availableStopes.length })}
      </Typography>
      <List
        dense
        sx={{
          flexGrow: 1,
        }}
      >
        <AutoSizer>
          {({ height, width }: { height: number; width: number }) => (
            <ListAny
              className="List"
              height={height}
              itemCount={availableStopes.length}
              itemSize={120}
              width={width}
              itemData={availableStopes}
            >
              {Row}
            </ListAny>
          )}
        </AutoSizer>
      </List>
    </Box>
  );
};

export default StopeList;
